import classes from "./ActivityEditor.module.css";
import {EditorContent, useEditor} from "@tiptap/react";
import {delay, notifyError, stripHTMLTags} from "../../utils/helpers";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import {
    GRADE_SCHEMA,
    imageSuffixArr,
    POLL_TYPE,
    QUESTION_TITLE_CHARACTER_LIMIT,
    QUESTION_TYPES,
    videoSuffixArr
} from "../../utils/constants";
import TextStyle from "@tiptap/extension-text-style";
import {Color} from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import React, {useEffect, useRef, useState} from "react";
import TiptapClasses from "../../components/Tiptap/Tiptap.module.css";
import {TipTapMenuBarNew} from "../../components/Tiptap/TipTapMenuBarNew";
import {ReactComponent as ATTACHMENT} from "../../images/attachment_grey.svg";
import {ReactComponent as BLINK} from "../../images/blink_purple.svg";
import {extractOriginalImageName, useImageLoaderManager} from "../../hooks/useImageLoaderManager";
import {PollApiCalls} from "../../apis/PollApiCalls";
import ActivityEditorQuestionMediaViewer from "./ActivityEditorQuestionMediaViewer";
import { initialQuestionData} from "../../utils/questionUtils";
import FITBQuestionTitleNew from "../../questions/FITB/FITBQuestionTitleNew";
import YuJaButton from "../../components/standardization/YuJaButton";
import QuestionGenerateModal from "../../components/modals/QuestionGenerateModal";
import {useLocalStorageNew} from "../../hooks/useLocalStorageNew";
import { useTranslation } from "react-i18next";

export default function ActivityEditorQuestionTitle({
                                                        draftActivity,
                                                        setDraftActivity,
                                                        getPollKey,
                                                        currQueDisplay,
                                                        setQuestions,
                                                        shownQuestionType,
                                                        handleTitleUpdate,
                                                        placeholder,
                                                        questionBankId = null,
                                                        savePoll,
                                                        disabled = false,
                                                    }) {
    const { t } = useTranslation();
    const {pollKey="", pollType, gradeSchema, questions = []} = draftActivity;
    let selectedQuestion = questions.find(q => q.serialNo === currQueDisplay);
    selectedQuestion = selectedQuestion.questionType === shownQuestionType ? selectedQuestion : initialQuestionData(shownQuestionType);
    const [ ,getHostResource] = useLocalStorageNew(window.location.hostname, {});
    const {platformSetting={}} = getHostResource();
    const {privateEVPLink=""} = platformSetting;



    const {serialNo, queTitle, image, imageURL, directLinkEvp, questionType} = selectedQuestion;

    const fileName = extractOriginalImageName(image);

    const {
        createUniqueImage,
        resetImage,
        setImage,
        setQuestionImageLoading,
        checkImageIsLoading,
        setQuestionVideoLoading,
        checkVideoIsLoading,
    } = useImageLoaderManager(questions, setQuestions);

    const editor = useEditor({
        shouldRerenderOnTransaction: true,
        editorProps: {
            attributes: {
                'aria-label': `Question Title Rich Text Editor`,
                'aria-multiline': 'true',
                'aria-readonly': 'false',
            },
            transformPastedHTML(text) {
                return stripHTMLTags(text);
            },
        },
        extensions: [
            Underline,
            StarterKit,
            Placeholder.configure({
                placeholder,
            }),
            CharacterCount.configure({limit: QUESTION_TITLE_CHARACTER_LIMIT}),
            TextStyle,
            Color,
            Highlight.configure({multicolor: true}),
            // Mathematics.configure({
            //     regex: LATEX_REG_EXP,
            // }),
        ],
        onUpdate: ({editor}) => {
            handleTitleUpdate(editor.getHTML());
        },
        content: queTitle,
    }, [serialNo]);
    const {uploadQueImage, uploadQueBankImage, uploadVideo, getUploadLinkForVideo} = PollApiCalls();
    const mediaInputRef = useRef();
    const [questionGenerateModal, setQuestionGenerateModal] = useState(false);


    useEffect(() => {
        if (shownQuestionType === QUESTION_TYPES.FITB.name) {
            return;
        }
        if (queTitle !== editor.getHTML()) {
            editor.commands.setContent(queTitle);
        }

        editor.options.editable = !disabled;
    }, [queTitle, editor, disabled, shownQuestionType]);

    useEffect(() => {
        setQuestionGenerateModal(false);
    }, [serialNo]);

    const handleVideoUpload = async (file) => {
        try {
            // setVideoUploading(true);
            setQuestionVideoLoading(selectedQuestion.serialNo);
            // 1. Get upload link
            const res = await getUploadLinkForVideo();

            // 2. PUT to s3 bucket
            await fetch(res.url, {
                headers: {
                    'Content-Type': "Video/mp4",
                },
                method: "PUT",
                body: file
            });

            // 3. POST /upload/video/
            const res3 = await uploadVideo(res.key, file.name, pollKey, selectedQuestion.serialNo);
            if (res3) {
                selectedQuestion.directLinkEvp = res3;

                let newQuestions = [];
                questions.forEach((q) => {
                    if (q.serialNo === selectedQuestion.serialNo) {
                        const newQuestion = {...q};
                        newQuestion.image = file.name;
                        newQuestion.imageURL = "";
                        newQuestion.directLinkEvp = res3;
                        newQuestions.push(newQuestion);
                    } else {
                        newQuestions.push({...q});
                    }
                });

                if (!!savePoll) {
                    savePoll(newQuestions);
                } else {
                    setQuestions(newQuestions);
                }
            }
        } catch (e) {
            console.log(e);
            notifyError(t("questionBuilder.uploadVideoError"));
            setQuestionVideoLoading(selectedQuestion.serialNo, true);
        }

    };

    const upload = async (e) => {
        let file = e.target.files[0];
        const suffix = file.name.substring(file.name.lastIndexOf('.'))?.toLowerCase();
        if (selectedQuestion.questionType === QUESTION_TYPES.CI.name && (!imageSuffixArr.includes(suffix) || videoSuffixArr.includes(suffix))) {
            notifyError(t("questionBuilder.clickableImageFormatError"));
            return;
        }

        if (!imageSuffixArr.includes(suffix) && !videoSuffixArr.includes(suffix)) {
            notifyError(t("questionBuilder.invalidFileFormat"));
            mediaInputRef.current.value = '';
            return;
        }

        if (!file || !file.name) { // if no image is selected
            notifyError(t("questionBuilder.selectFileError"));
            return;
        }

        // setFileName(file.name);
        const newFileName = createUniqueImage(file.name);
        const f = new File([file], newFileName); // Make new File with name temp-<currQueDisplay>.<suffix>
        // updateSuffix(suffix);
        if (videoSuffixArr.includes(suffix)) {
            await handleVideoUpload(new File([file], file.name));
        } else if (!questionBankId) {
            let uuid = getPollKey();
            uploadQueImage(uuid, f, (name, url) => {
                setImage(name, url);
            }).then(res => {
                if (!res) {
                    resetImage(newFileName);
                }
            });
            setQuestionImageLoading(selectedQuestion.serialNo, newFileName);
        } else if (!!questionBankId) {
            uploadQueBankImage(questionBankId, f, (name, url) => {
                setImage(name, url);
            }).then(res => {
                if (!res) {
                    resetImage(fileName);
                }
            });
            setQuestionImageLoading(selectedQuestion.serialNo, newFileName);
        }

        if (mediaInputRef.current) {
            mediaInputRef.current.value = '';
        }
    };

    if (shownQuestionType === QUESTION_TYPES.FITB.name) {
        return (
            <>
                <FITBQuestionTitleNew
                    gradeSchema={gradeSchema}
                    selectedQuestion={selectedQuestion}
                    questions={questions}
                    setQuestions={setQuestions}
                    upload={upload}
                    hoverDisplay={shownQuestionType !== questionType}
                >
                    <ActivityEditorQuestionMediaViewer
                        draftActivity={draftActivity}
                        setDraftActivity={setDraftActivity}
                        shownQuestionType={shownQuestionType}
                        selectedQuestion={selectedQuestion}
                        setQuestionVideoLoading={setQuestionVideoLoading}
                        checkImageIsLoading={checkImageIsLoading}
                        checkVideoIsLoading={checkVideoIsLoading}
                        savePoll={savePoll}
                        questionBankId={questionBankId}
                        setQuestions={setQuestions}
                    />
                </FITBQuestionTitleNew>
            </>

        );
    }


    return (
        <>
            <QuestionGenerateModal
                gradeSchema={gradeSchema}
                show={questionGenerateModal}
                setModalShow={setQuestionGenerateModal}
                questions={questions}
                setQuestions={setQuestions}
                selectedQuestion={selectedQuestion}
            />
            <div className={classes.questionTitleEditorBox}>
                <div className={classes.questionTitleEditorMenuBar}>
                    <TipTapMenuBarNew editor={editor} selectedQuestion={selectedQuestion} disabled={disabled}/>
                    {!directLinkEvp && !imageURL && !disabled && pollType !== POLL_TYPE.ATTENDANCE &&
                        <>
                            {!!privateEVPLink && shownQuestionType !== QUESTION_TYPES.CI.name &&
                                <YuJaButton
                                    wrapperStyle={{whiteSpace: 'nowrap'}}
                                    style={{width: 180, height: 35, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 10}}
                                    onClick={() => setQuestionGenerateModal(true)}
                                >
                                    <BLINK style={{flexShrink: 0, marginRight: 3}}/> {t("genQuestion.generateQuestionText")}
                                </YuJaButton>
                            }

                            <span
                                tabIndex={0}
                                className={classes.mediaChooserBar}
                                onClick={(e) => mediaInputRef.current.click(e)}
                            >
                                <ATTACHMENT style={{marginRight: 2}}/>
                                    {shownQuestionType === QUESTION_TYPES.CI.name ? t("questionBuilder.chooseImageText") : t("institution.chooseMediaText")}
                                    <input
                                        ref={mediaInputRef}
                                        style={{display: "none"}}
                                        type="file"
                                        accept={shownQuestionType === QUESTION_TYPES.CI.name
                                            ? "image/png, image/jpg, image/jpeg, image/jfif, image/pjpeg, image/pjp"
                                            : "image/png, image/jpg, image/jpeg, image/jfif, image/pjpeg, image/pjp, video/*"}
                                        onChange={upload}
                                    />
                            </span>
                        </>
                    }

                </div>

                <EditorContent
                    editor={editor}
                    style={{minHeight: 100, paddingBottom: 6}}
                    className={TiptapClasses.editorContentBox}
                />
                <span className={classes.characterCountBox}>
                    {editor.storage.characterCount.characters()}/{QUESTION_TITLE_CHARACTER_LIMIT}
                </span>
            </div>


            <ActivityEditorQuestionMediaViewer
                draftActivity={draftActivity}
                setDraftActivity={setDraftActivity}
                shownQuestionType={shownQuestionType}
                selectedQuestion={selectedQuestion}
                setQuestionVideoLoading={setQuestionVideoLoading}
                checkImageIsLoading={checkImageIsLoading}
                checkVideoIsLoading={checkVideoIsLoading}
                savePoll={savePoll}
                questionBankId={questionBankId}
                setQuestions={setQuestions}
            />
        </>
    );
}